import { FormEvent, useEffect, useState } from 'react';
import { CategoriesApi } from '../../api/resources';
import { ApiError } from '../../api/client';
import type { Category } from '../../api/types';
import { useToast } from '../../context/ToastContext';

const emptyForm = { code: '', name: '', description: '' };

export function Categories() {
  const { notify } = useToast();
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  async function load() {
    setLoading(true);
    try {
      const result = await CategoriesApi.list(true);
      setCategories(result.categories);
    } catch (err) {
      notify(err instanceof ApiError ? err.message : 'No fue posible cargar las categorias.', 'error');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
  }, []);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setSaving(true);
    try {
      await CategoriesApi.create({ code: form.code, name: form.name, description: form.description || undefined });
      notify('Categoria creada.', 'success');
      setForm(emptyForm);
      setShowForm(false);
      load();
    } catch (err) {
      notify(err instanceof ApiError ? err.message : 'No fue posible crear la categoria.', 'error');
    } finally {
      setSaving(false);
    }
  }

  async function toggleActive(c: Category) {
    try {
      if (c.isActive) await CategoriesApi.deactivate(c.id);
      else await CategoriesApi.activate(c.id);
      notify('Estado actualizado.', 'success');
      load();
    } catch (err) {
      notify(err instanceof ApiError ? err.message : 'No fue posible actualizar el estado.', 'error');
    }
  }

  return (
    <div>
      <div className="flex-between">
        <h1>Categorias</h1>
        <button type="button" className="btn btn-primary" onClick={() => setShowForm((v) => !v)}>
          {showForm ? 'Cerrar' : '+ Nueva categoria'}
        </button>
      </div>

      {showForm && (
        <div className="card">
          <form onSubmit={handleSubmit} noValidate>
            <div className="form-grid">
              <div className="field">
                <label htmlFor="ccode">Codigo</label>
                <input id="ccode" required value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} />
              </div>
              <div className="field">
                <label htmlFor="cname">Nombre</label>
                <input id="cname" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
              </div>
              <div className="field">
                <label htmlFor="cdesc">Descripcion</label>
                <input id="cdesc" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
              </div>
            </div>
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? 'Guardando...' : 'Crear categoria'}
            </button>
          </form>
        </div>
      )}

      <div className="card">
        {loading ? (
          <div className="loading-block">Cargando...</div>
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Codigo</th>
                  <th>Nombre</th>
                  <th>Descripcion</th>
                  <th>Estado</th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {categories.map((c) => (
                  <tr key={c.id}>
                    <td>{c.code}</td>
                    <td>{c.name}</td>
                    <td>{c.description || '-'}</td>
                    <td>
                      <span className={`pill ${c.isActive ? 'pill-active' : 'pill-inactive'}`}>{c.isActive ? 'Activa' : 'Inactiva'}</span>
                    </td>
                    <td>
                      <button type="button" className={`btn btn-sm ${c.isActive ? 'btn-danger' : 'btn-secondary'}`} onClick={() => toggleActive(c)}>
                        {c.isActive ? 'Desactivar' : 'Activar'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
